import { Box, Button, Dialog, DialogTitle, Divider } from "@mui/material";
import { useEffect } from "react";
import { BindingDataDto, ParseAlgorithmDto } from "../../types";
import useAuth from "../../hoc/AuthProvider/useAuth";
import { Permissions } from "../../config/permissions";
import IconButton from '@mui/material/IconButton';
import CustomSnackbar from "../Common/CustomSnackbar";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import PayloadCard from "./PayloadCard";
import ParameterBindingSection from "./ParameterBindingSection";
import ParsedDataTable from "./ParsedDataTable";
import { useHexDataParser } from "./hooks/useHexDataParser";
import { useDataTypes } from "./hooks/useDataTypes";
import { useBindingManagement } from "./hooks/useBindingManagement";
import { usePayloadConversion } from "./hooks/usePayloadConversion";
import { validateSave } from "../../utils/validation";
import { useSnackbar } from "../Common/hooks/useSnackbar";
import { getDisplayIndex } from "../../utils/hexParser/helpers";
import { outlinedPrimaryBtnSx, containedPrimaryBtnSx } from "../Common/styles";

type Prop = {
  hexData: string;
  keyValueId: number;
  open: boolean;
  showDeviceId?: boolean;
  onClose: () => void;
  onSave: (rules: ParseAlgorithmDto[], bindingRules: BindingDataDto[]) => void;
  rules?: ParseAlgorithmDto[];
  bindingRules?: BindingDataDto[];
}

const HexRawDataParse: React.FC<Prop> = ({
  hexData,
  keyValueId,
  open,
  showDeviceId,
  onClose,
  onSave,
  rules,
  bindingRules
}) => {
  const { showError, snackbarProps } = useSnackbar();
  const { havePermission } = useAuth();
  const canSave = havePermission(Permissions.ParserConfig_Update);

  const {
    rawData,
    rawBaseData,
    parsed,
    setParsed,
    setRawData,
    handleAddRow,
    handleAddChildRow,
    handleStartChange,
    handleEndChange,
    handleTypeChange,
    handleToTypeChange,
    handleParameterTypeChange,
    handleDescriptionChange,
    handleDeleteParsedRow,
    handleIsDeviceIdChange,
    loadParseAlgorithm,
    buildRules,
  } = useHexDataParser(hexData, open, rules, bindingRules);

  const {
    dataType,
    parsedType,
    selectedParseTypeId,
    selectedConvertTypeId,
    selectedParseType,
    selectedConvertType,
    handleParseType,
    handleConvertType,
    setSelectedParseTypeId,
    setSelectedConvertTypeId,
    resetTypes,
  } = useDataTypes();

  const {
    bindings,
    setBindings,
    getIndexLabel: getIndexLabelBase,
    getBindingPreview,
    loadBindingRules,
    buildBindingRules,
    resetBindings,
  } = useBindingManagement(open, parsed, bindingRules);


  usePayloadConversion(
    hexData,
    selectedParseType,
    selectedConvertType,
    setRawData,
    setParsed,
    rawData,
    (msg) => showError(msg)
  );

  const getIndexLabel = (id: string) => {
    return getIndexLabelBase(id, getDisplayIndex);
  };

  const handleSave = () => {
    const validation = validateSave(parsed, bindings, showDeviceId);
    if (!validation.isValid) {
      showError(validation.errorMessage);
      return;
    }

    onSave(
      buildRules(selectedParseType!.id, selectedConvertType!.id),
      buildBindingRules()
    );
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    if (!rules || rules.length === 0) return;
    if (parsedType.length === 0) return;

    const loadData = async () => {
      try {
        setSelectedParseTypeId(rules[0].parseDataTypeId ?? null);
        setSelectedConvertTypeId(rules[0].convertDataTypeId ?? null);

        const parsedArray = await loadParseAlgorithm();
        if (parsedArray) {
          loadBindingRules(parsedArray, bindingRules);
        }
      } catch (error) {
        showError(error as Error);
      }
    };

    loadData();
  }, [open, parsedType, rules]);

  useEffect(() => {
    if (!open) {
      resetTypes();
      resetBindings();
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={true}
        maxWidth={"xl"}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
            backgroundImage: "linear-gradient(#fafafa, #ffffff)"
          }
        }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          Data Parse
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <PayloadCard
          rawBaseData={rawBaseData}
          rawData={rawData}
          parsedType={parsedType}
          selectedParseType={selectedParseType}
          selectedConvertType={selectedConvertType}
          onParseTypeChange={handleParseType}
          onConvertTypeChange={handleConvertType}
          selectedParseTypeId={selectedParseTypeId}
          selectedConvertTypeId={selectedConvertTypeId}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            px: 2,
            py: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={handleAddRow}
            disabled={!selectedParseType || !selectedConvertType || rawData === ""}
            sx={outlinedPrimaryBtnSx}
          >
            Ekle
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={containedPrimaryBtnSx}
          >
            Kaydet
          </Button>
        </Box>

        <ParsedDataTable
          parsed={parsed}
          dataType={dataType}
          onStartChange={handleStartChange}
          onEndChange={handleEndChange}
          onParameterTypeChange={handleParameterTypeChange}
          onTypeChange={handleTypeChange}
          onToTypeChange={handleToTypeChange}
          onDescriptionChange={handleDescriptionChange}
          onIsDeviceIdChange={handleIsDeviceIdChange}
          showDeviceId={showDeviceId ?? false}
          onDeleteRow={handleDeleteParsedRow}
          onAddChildRow={handleAddChildRow}
          getDisplayIndex={getDisplayIndex}
        />
        <Box sx={{ mt: 2, px: 2 }}>
          <ParameterBindingSection
            bindings={bindings}
            parsed={parsed}
            onBindingsChange={setBindings}
            getIndexLabel={getIndexLabel}
            getBindingPreview={getBindingPreview}
          />
        </Box>
        <CustomSnackbar {...snackbarProps} />
      </Dialog>
    </>
  );
}

export default HexRawDataParse;
